import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLists, useCreateList, useUpdateList, useDeleteList } from './useListsQuery.js';
import { useUIStore, closeMobileSidebar, dismissMobileSidebarForNavigation } from '../../store/ui.store.js';

export default function Sidebar() {
  const { data: lists = [], isLoading } = useLists();
  const createList = useCreateList();
  const updateList = useUpdateList();
  const deleteList = useDeleteList();
  const { activeListId, setActiveList, sidebarOpen } = useUIStore();
  const [newListName, setNewListName] = useState('');
  const [showInput, setShowInput] = useState(false);
  const [listsExpanded, setListsExpanded] = useState(true);
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === '/' || location.pathname === '/dashboard';
  const onTasks = location.pathname === '/tasks';

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newListName.trim()) return;
    await createList.mutateAsync({ name: newListName.trim() });
    setNewListName('');
    setShowInput(false);
  }

  function startEditing(listId: string, currentName: string) {
    setConfirmDeleteId(null);
    setEditingListId(listId);
    setEditName(currentName);
  }

  async function handleRename(e: React.FormEvent) {
    e.preventDefault();
    if (!editingListId || !editName.trim()) return;
    await updateList.mutateAsync({ listId: editingListId, payload: { name: editName.trim() } });
    setEditingListId(null);
  }

  function handleDelete(listId: string) {
    deleteList.mutate(listId);
    setConfirmDeleteId(null);
  }

  return (
    <>
      {sidebarOpen && <div className="sidebar-backdrop" onClick={closeMobileSidebar} />}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''}`}>
      <nav className="sidebar__nav">
        <button
          className={`sidebar__item ${onHome ? 'sidebar__item--active' : ''}`}
          onClick={() => { navigate('/'); dismissMobileSidebarForNavigation(); }}
        >
          Home
        </button>
        <button
          type="button"
          className="sidebar__item sidebar__section-toggle"
          onClick={() => setListsExpanded((v) => !v)}
          aria-expanded={listsExpanded}
        >
          <span className="sidebar__accordion-glyph">{listsExpanded ? '−' : '+'}</span>
          My Lists
        </button>

        {listsExpanded && (
          <>
            {isLoading && <div className="sidebar__loading">Loading...</div>}

            {lists.map((list) =>
              editingListId === list.listId ? (
                <form key={list.listId} onSubmit={handleRename} className="sidebar__new-list sidebar__rename-form">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Escape') setEditingListId(null); }}
                  />
                  <button type="submit" className="btn btn--primary btn--sm" disabled={updateList.isPending}>
                    ✓
                  </button>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditingListId(null)}>
                    ✕
                  </button>
                </form>
              ) : confirmDeleteId === list.listId ? (
                <div key={list.listId} className="sidebar__confirm-delete">
                  <span>Delete "{list.name}" and its tasks?</span>
                  <div className="sidebar__confirm-delete-actions">
                    <button
                      className="btn btn--sm btn--danger"
                      onClick={() => handleDelete(list.listId)}
                      disabled={deleteList.isPending}
                    >
                      Delete
                    </button>
                    <button className="btn btn--sm btn--ghost" onClick={() => setConfirmDeleteId(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div key={list.listId} className="sidebar__list-row">
                  <button
                    className={`sidebar__item ${onTasks && activeListId === list.listId ? 'sidebar__item--active' : ''}`}
                    onClick={() => { navigate('/tasks'); setActiveList(list.listId); dismissMobileSidebarForNavigation(); }}
                    style={{ borderLeft: `3px solid ${list.color}` }}
                  >
                    {list.name}
                  </button>
                  <span className="sidebar__list-actions">
                    <button
                      className="sidebar__icon-btn"
                      onClick={() => startEditing(list.listId, list.name)}
                      aria-label={`Edit ${list.name}`}
                      title="Edit list"
                    >
                      ✎
                    </button>
                    <button
                      className="sidebar__icon-btn sidebar__icon-btn--danger"
                      onClick={() => setConfirmDeleteId(list.listId)}
                      aria-label={`Delete ${list.name}`}
                      title="Delete list"
                    >
                      🗑
                    </button>
                  </span>
                </div>
              )
            )}

            {showInput ? (
              <form onSubmit={handleCreate} className="sidebar__new-list sidebar__rename-form">
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="List name"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Escape') setShowInput(false); }}
                />
                <button type="submit" className="btn btn--primary btn--sm" disabled={createList.isPending}>
                  ✓
                </button>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowInput(false)}>
                  ✕
                </button>
              </form>
            ) : (
              <button
                type="button"
                className="sidebar__item sidebar__add-list"
                onClick={() => setShowInput(true)}
              >
                + New List
              </button>
            )}
          </>
        )}

        <button
          className={`sidebar__item ${onTasks && activeListId === null ? 'sidebar__item--active' : ''}`}
          onClick={() => { navigate('/tasks'); setActiveList(null); dismissMobileSidebarForNavigation(); }}
        >
          All Tasks
        </button>
      </nav>
      </aside>
    </>
  );
}
